import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

export function useConSchedulePresets() {
    const [rows,    setRows]    = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const cached = await loadReferenceCache('con_schedule_presets', null).catch(() => null)
        if (cached) {
            setRows(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('con_schedule_presets')
            .select('*')
            .order('sort_order', { ascending: true })
            .order('updated_at', { ascending: false })
        if (err) {
            console.error('[conSchedulePresets] fetch error:', err.message)
            setError(err.message)
            if (!cached) setRows([])
        } else {
            setRows(data || [])
            await saveReferenceCache('con_schedule_presets', data || [], null).catch(() => {})
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    const createSchedulePreset = useCallback(async (fields) => {
        const nextSort = rows.length > 0 ? Math.max(...rows.map(r => r.sort_order || 0)) + 1 : 0
        const payload = {
            name:       fields.name       || 'Untitled',
            rows:       fields.rows       || [],
            sort_order: fields.sort_order ?? nextSort,
            updated_at: new Date().toISOString(),
        }
        const { data, error: err } = await supabase
            .from('con_schedule_presets')
            .insert(payload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => [...prev, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)))
        await clearReferenceCache('con_schedule_presets', null).catch(() => {})
        return data
    }, [rows])

    const updateSchedulePreset = useCallback(async (id, fields) => {
        const payload = {
            name:       fields.name,
            rows:       fields.rows,
            sort_order: fields.sort_order,
            updated_at: new Date().toISOString(),
        }
        Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k] })

        const { data, error: err } = await supabase
            .from('con_schedule_presets')
            .update(payload)
            .eq('id', id)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => prev.map(r => r.id === id ? data : r).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)))
        await clearReferenceCache('con_schedule_presets', null).catch(() => {})
        return data
    }, [])

    const deleteSchedulePreset = useCallback(async (id) => {
        const { error: err } = await supabase
            .from('con_schedule_presets')
            .delete()
            .eq('id', id)
        if (err) throw new Error(err.message)
        setRows(prev => prev.filter(r => r.id !== id))
        await clearReferenceCache('con_schedule_presets', null).catch(() => {})
    }, [])

    const setDefaultPreset = useCallback(async (id) => {
        // Optimistic update
        setRows(prev => prev.map(r => ({ ...r, is_default: r.id === id })))
        // Clear all defaults first, then set the target
        const { error: clearErr } = await supabase
            .from('con_schedule_presets')
            .update({ is_default: false, updated_at: new Date().toISOString() })
            .neq('id', id)
        if (clearErr) throw new Error(clearErr.message)
        const { data, error: setErr } = await supabase
            .from('con_schedule_presets')
            .update({ is_default: true, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select()
            .single()
        if (setErr) throw new Error(setErr.message)
        setRows(prev => prev.map(r => r.id === id ? data : { ...r, is_default: false }))
        await clearReferenceCache('con_schedule_presets', null).catch(() => {})
        return data
    }, [])

    const clearDefaultPreset = useCallback(async (id) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, is_default: false } : r))
        const { data, error: err } = await supabase
            .from('con_schedule_presets')
            .update({ is_default: false, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => prev.map(r => r.id === id ? data : r))
        await clearReferenceCache('con_schedule_presets', null).catch(() => {})
    }, [])

    return { rows, loading, error, createSchedulePreset, updateSchedulePreset, deleteSchedulePreset, setDefaultPreset, clearDefaultPreset, reload: load }
}
